<template>
    <div>
        <h2 class="title is-marginless">Payment Methods</h2>
        <loading v-if="loading"></loading>
        <div v-else>

            <div class="methods-con" v-if="!payments.accountProvider">
                <div class="message is-warning">
                    <div class="message-body">
                        Sorry but we don't currently have any way to accept payments, please contact us.
                    </div>
                </div>
            </div>
            <div class="methods-con" v-else-if="!activeMethods.length">
                <div class="message is-warning">
                    <div class="message-body">
                        You don't have any payment methods setup yet
                    </div>
                </div>
            </div>

            <draggable v-show="activeMethods.length" class="methods-con" :list="payments.dealerMethods" v-bind="{ dragClass : '.rank' }" @end="changeRank">
                <div v-for="method in activeMethods" :key="method.id" class="method">
                    <div class="columns is-marginless">
                        <div class="column">
                            <div class="rank">
                                <div class="tag is-large is-dark">{{method.rank}}</div>
                            </div>
                            {{method.displayTitle}}
                        </div>
                        <div class="column is-narrow has-text-right">
                            <div class="flex-con sm-pad-lr">
                                <div
                                    class="payment-active-con tooltip is-tooltip-left"
                                    @click="confirmActivatePaymentMethod(method)"
                                    :data-tooltip="method.active ? 'Currently active, click to deactivate' : 'Currently inactive, click to activate'"
                                >
                                    <div class="icon fa" :class="method.active ? 'fa-toggle-on' : 'fa-toggle-off'"></div>
                                </div>
                                <div>
                                    <button class="button is-danger is-small" @click="confirmDeletePaymentMethod(method)">
                                        <span class="icon">
                                            <i class="fa fa-trash"></i>
                                        </span>
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </draggable>

            <confirm-modal
                :active="activatePaymentMethodHelper.active"
                :callback="activatePaymentMethodHelper.callback"
                :loading="activatePaymentMethodHelper.loading"
                :close="() => { activatePaymentMethodHelper.active = false}"
            >
                <div class="has-text-centered">
                    <h2 class="is-size-4">Are you sure you want to set this payment method to {{activatePaymentMethodHelper.method.active ? 'inactive' : 'active'}}?</h2>
                    <div
                        v-show="activatePaymentMethodHelper.method.active && paymentRemovalWillTurnOffAutoAuth"
                        class="message is-warning m-t-md"
                    >
                        <div class="message-body">
                            Since this is your only active payment method deactivating it will turn off your auto authorizing invoice setting for new orders
                            and will set any of your currently authorized orders to unauthorized.
                        </div>
                    </div>
                </div>
            </confirm-modal>

            <confirm-modal
                :active="deletePaymentMethodHelper.active"
                :callback="deletePaymentMethodHelper.callback"
                :loading="deletePaymentMethodHelper.loading"
                :close="() => { deletePaymentMethodHelper.active = false}"
            >
                <div class="has-text-centered">
                    <h2 class="is-size-4">Are you sure you want to remove this payment method?</h2>
                    <div
                        v-show="deletePaymentMethodHelper.method.active && paymentRemovalWillTurnOffAutoAuth"
                        class="message is-warning m-t-md"
                    >
                        <div class="message-body">
                            Since this is your only active payment method deactivating it will turn off your auto authorizing invoice setting for new orders
                            and will set any of your currently authorized orders to unauthorized.
                        </div>
                    </div>
                </div>
            </confirm-modal>

            <div class="changed-rank-con" v-show="rankChanged">
                <div class="button is-success" :class="{ 'is-loading' : rankSaving }" @click="saveRank()">
                    Save Payment Priorities
                </div>
            </div>

            <div class="bottom-con">

                <div v-if="ccMethod">
                    <div v-if="addingCard" class="add-card-con">
                        <stripe-card
                            v-if="isStripe"
                            :active-provider="activeProvider"
                            :cc-method="ccMethod"
                            @cancel="addingCard = false"
                            @create-payment-method="createCreditCard"
                        ></stripe-card>

                        <square-card
                            v-else-if="isSquare"
                            id="1"
                            :active-provider="activeProvider"
                            :cc-method="ccMethod"
                            @create-payment-method="createCreditCard"
                        ></square-card>

                    </div>
                    <div
                        v-else
                        class="button is-primary"
                        @click="addingCard = true"
                    >
                        Add New Card
                    </div>
                </div>
            </div>

        </div>
    </div>
</template>

<script>
import Dealer from '../../../api/Dealer';
import DealerSettingsApi from '../../../api/DealerSettings';
import StripeCard from './StripeCard';
import EventBus from '../../../event-bus';
import _ from 'underscore';
import draggable from 'vuedraggable'
import SquareCard from "@/views/settings/components/SquareCard";

const stripeACHDefaults = () => {
    return {
        account_holder_name : null,
        account_holder_type : 'company',
        routing_number : null,
        account_number : null,
    };
};

export default {
    name : "PaymentMethods",
    components : {
        SquareCard,
        draggable,
        StripeCard
    },
    data () {
        return {
            loading : true,
            payments : {
                dealerMethods : [],
                accountProvider : [],
            },
            complete : false,
            addingCard : false,
            savingCard : false,
            rankChanged : false,
            rankSaving : false,
            stripeOptions : {
                // see https://stripe.com/docs/stripe.js#element-options for details
            },
            savingInvoiceSettings : false,
            stripeACHHelper : {
                show : false,
                loading : false,
                data : stripeACHDefaults(),
            },
            deletePaymentMethodHelper : {
                active : false,
                loading : false,
                method : {},
                callback : () => {},
            },
            activatePaymentMethodHelper : {
                active : false,
                loading : false,
                method : {},
                callback : () => {},
            },

        }
    },

    beforeMount () {
        DealerSettingsApi.getPaymentMethods().then(response => {
            this.payments.accountProvider = response.data.accountProvider;
            this.payments.dealerMethods = response.data.dealerMethods.map(dm => {
                if(dm.metaData) {
                    dm.metaData = JSON.parse(dm.metaData);
                }
                return dm;
            });
            this.loading = false;
        });
    },

    computed : {

        activeMethods () {
            const methods = [];
            this.payments.dealerMethods.forEach((method) => {
                // exclude ACH methods
                if (this.achMethod && method.paymentProviderMethodId === this.achMethod.id) {
                    return;
                }
                const displayMethod = {
                    id : method.id,
                    rank : methods.length + 1,
                    displayTitle : method.last4,
                    active : method.active,
                };
                if (this.ccMethod && method.paymentProviderMethodId === this.ccMethod.id) {
                    displayMethod.displayTitle = `${method.metaData.card.brand} ending in ${method.last4}, expires on ${method.metaData.card.exp_month}/${method.metaData.card.exp_year}`;
                }
                methods.push(displayMethod);
            });
            return methods;
        },

        numberOfActivePaymentMethods () {
            return _.where(this.activeMethods, { active : true }).length;
        },

        paymentRemovalWillTurnOffAutoAuth () {
            return this.numberOfActivePaymentMethods === 1;
        },

        activeProvider () {
            return this.payments.accountProvider;
        },

        isStripe() {
            if(!this.activeProvider) {
                return null;
            }
            return this.activeProvider.handle == 'stripe';
        },

        isSquare() {
            if(!this.activeProvider) {
                return null;
            }
            return this.activeProvider.handle == 'square';
        },

        ccMethod () {
            if(!this.activeProvider) {
                return null;
            }
            return _.findWhere(this.activeProvider.providerMethods, { handle : 'cc' }) || null;
        },

        achMethod () {
            if(!this.activeProvider) {
                return null;
            }
            return _.findWhere(this.activeProvider.providerMethods, { handle : 'ach' }) || null;
        },

        stripeToken () {
            if(!this.activeProvider || !this.ccMethod) {
                return null;
            }
            return _.findWhere(this.activeProvider.settings, { key : 'stripe_public_token' }).value;
        },

    },

    methods : {

        changeRank () {
            this.rankChanged = true;
        },

        saveRank () {
            this.rankSaving = true;
            const methods = _.map(this.activeMethods, method => {
                return {
                    id : method.id,
                    rank : method.rank,
                };
            });
            DealerSettingsApi.savePaymentMethodRanks(methods).finally(() => {
                this.$notify({
                    text : 'Payment Priorities Updated',
                });
                this.rankSaving = false;
                this.rankChanged = false;
            });
        },

        async createCreditCard (request) {

            await this.createPaymentMethod(request.method, request.settings)
                .finally(() => {
                    this.$notify({
                        text : 'Card added',
                    });
                    this.addingCard = false;
                });
        },

        async createStripeACH (form) {

            if(!(await this.$validator.validateAll(form))){
                return;
            }

            this.stripeACHHelper.loading = true;

            const stripe = window.Stripe(this.stripeToken);

            stripe.createToken('bank_account', {...this.stripeACHHelper.data, ...{ country : 'US', currency : 'usd' }}).then(async data => {

                if(!data.token){
                    this.$notify({
                        text : data.message,
                        type : 'error',
                    });
                    return;
                }

                const customer_token = await Dealer.postStripeAttachSource(data.token.id);

                if(!customer_token){
                    return Promise.reject();
                }

                await this.createPaymentMethod(
                    {
                        paymentProviderId : this.activeProvider.id,
                        paymentProviderMethodId : this.achMethod.id,
                        name : data.token.bank_account.account_holder_name,
                        last4 : data.token.bank_account.last4,
                        metaData : JSON.stringify(data.token),
                        active : false,
                        verifiedAt : null,
                    },
                    [
                        {
                            key : 'stripe_source_token',
                            value : data.token.bank_account.id,
                            accountPaymentProviderId : this.activeProvider.accountPaymentProvider.id,
                        },
                        {
                            key : 'stripe_ach_bank_account_id',
                            value : data.token.id,
                            accountPaymentProviderId : this.activeProvider.accountPaymentProvider.id,
                        },
                        {
                            key : 'stripe_customer_token',
                            value : customer_token,
                            accountPaymentProviderId : this.activeProvider.accountPaymentProvider.id,
                        }
                    ]
                );

                //TODO convert this to a modal / in the face messaging
                this.$notify({
                    text : 'ACH added',
                });

                this.stripeACHHelper.show = false;
                this.stripeACHHelper.data = stripeACHDefaults();

                //const customer_token = await Dealer.postStripeAttachSource(data.source.id);

            }).finally(() => {
                this.stripeACHHelper.loading = false;
            });

        },

        createPaymentMethod (newMethod, settings) {

            newMethod = {
                ...newMethod,
                id : null,
                accountId : this.$getAccount().id,
                accountDealerId : this.$getDealer().id,
                name : null,
                rank : this.activeMethods.length ? _.max(this.activeMethods, 'rank').rank + 1 : 1,
                active : newMethod.active === undefined ? true : newMethod.active,
                verifiedAt : newMethod.verifiedAt === undefined ? new Date() : newMethod.verifiedAt,
                settings : _.map(settings, setting => {
                    return {
                        ...setting,
                        id : null,
                        paymentMethodId : null, //will be set once method is created below
                        paymentProviderId : newMethod.paymentProviderId,
                        paymentProviderMethodId : newMethod.paymentProviderMethodId,
                    };
                }),

            };

            return DealerSettingsApi.savePaymentMethod(newMethod).then(response => {
                newMethod.id = response.data.id;
                if(newMethod.metaData) {
                    newMethod.metaData = JSON.parse(newMethod.metaData);
                }
                settings.forEach((setting, index) => {
                    Object.assign(setting, response.data.settings[index]);
                });
                newMethod.settings = settings;
                this.payments.dealerMethods.push(newMethod);
            });

        },

        confirmDeletePaymentMethod (method) {
            this.deletePaymentMethodHelper.method = {...method};
            this.deletePaymentMethodHelper.callback = (yes) => {
                if(yes){
                    this.deletePaymentMethodHelper.loading = true;
                    this.deletePaymentMethod(method).then(() => {
                        this.deletePaymentMethodHelper.loading = false;
                        this.deletePaymentMethodHelper.active = false;
                    });
                } else {
                    this.deletePaymentMethodHelper.active = false;
                }
            };
            this.deletePaymentMethodHelper.active = true;
        },

        deletePaymentMethod (method) {
            return DealerSettingsApi.deletePaymentMethod(method.id).then(() => {
                this.payments.dealerMethods = _.reject(this.payments.dealerMethods, { id : method.id });
                this.turnOffAutoAuthAfterPaymentRemoval();
                this.$notify({
                    text : 'Payment method has been removed',
                })
            });
        },

        confirmActivatePaymentMethod (method) {
            this.activatePaymentMethodHelper.method = {...method};
            this.activatePaymentMethodHelper.callback = (yes) => {
                if(yes){
                    this.activatePaymentMethodHelper.loading = true;
                    this.togglePaymentMethodActive(method).then(() => {
                        this.activatePaymentMethodHelper.loading = false;
                        this.activatePaymentMethodHelper.active = false;
                    });
                } else {
                    this.activatePaymentMethodHelper.active = false;
                }
            };
            this.activatePaymentMethodHelper.active = true;
        },

        togglePaymentMethodActive (method) {
            return DealerSettingsApi.togglePaymentMethodActive(method.id).then(() => {
                const existing = _.findWhere(this.payments.dealerMethods, { id : method.id });
                if(existing){
                    existing.active = !existing.active;
                    this.turnOffAutoAuthAfterPaymentRemoval();
                }
                this.$notify({
                    text : existing.active ? 'Payment method is now active' : 'Payment method has been deactivated',
                })
            });
        },

        turnOffAutoAuthAfterPaymentRemoval () {
            if(this.numberOfActivePaymentMethods > 0) {
                return;
            }
            EventBus.emit(EventBus.EVENT.ALL_PAYMENTS_DELETED);
        },

        async verifyStripeACHDeposits (method, form) {

            const isValid = await this.$validator.validateAll(form);

            if(!isValid){
                this.$notify({
                    text : 'Please enter valid deposit amounts',
                    type : 'error',
                });
                return;
            }

            this.$set(method, 'verifingACH', true);

            const payload = {
                paymentMethodId : method.id,
                deposit1 : method.deposit1,
                deposit2 : method.deposit2,
            };

            const verified = await Dealer.postStripeVerifyACH(payload);

            if(!verified){
                this.$notify({
                    text : 'Those deposit amounts don\'t match the actual deposits made to your bank account.',
                    type : 'error',
                });
            } else {
                this.$notify({
                    text : 'Your bank account has been verified',
                });

                //TODO delete a bunch of stuff below, will need to relook at
                /*
                const newValues = {
                    active : true,
                    verifiedAt : new Date(),
                };
                 */
            }

            this.$set(method, 'verifingACH', false);
            this.$forceUpdate();

        },


    },
}
</script>

<style lang="scss" scoped>
    @import '../../../assets/css/_theme';

    .methods-con {
        padding:20px 0;

        .method {
            //padding:20px;
            background-color: $white-ter;
            margin-bottom: 20px;
            border-radius: 5px;
            &:last-child {
                margin-bottom:0;
            }
            .rank {
                cursor:move;
                display: inline-block;
                min-width:60px;
                text-align:center;
                //margin-right: 10px;
                //background-color: $grey-lighter;
                //padding: 8px 12px;
                //border-radius: 100px;
            }

            .payment-active-con {
                cursor:pointer;
                .fa {
                    font-size:28px;
                    &.fa-toggle-on {
                        color:$green;
                    }
                    &.fa-toggle-off {
                        color: $grey-dark;
                    }
                }
            }
        }

    }

    .add-card-con {
        max-width:440px;
        padding:20px;
        background-color:$white-bis;
        border-radius:10px;
        box-shadow:0 0 4px -1px $grey-dark;
    }

    .changed-rank-con {
        padding-bottom:20px;
    }

    .ach-verify-deposit-con {
        padding:10px 20px;
        margin:20px 0 0 13px;
        border-left:4px solid $primary;
        .input {
            width:90px;
        }
    }

</style>
